import { useMemo } from 'react';
import { useDebounceEffect, useMemoizedFn } from 'ahooks';
import type { IAgentData } from '../../../../../AgentCollection/types';
import {
  ensureAgentInKnowledgeAgentList,
  filterAgentsByScene,
  filterAgentsForKnowledgeSelection,
} from '../../../../../AgentCollection/utils';
import { store } from '../../../../../ChatPage/hooks/useLocalState';
import type { ISelectedKnowledgeTagData } from '../../../KnowledgeBaseMention/components/SelectedKnowledgeTag';
import type { ISelectedSkillTagData } from '../../../SkillMention/SelectedSkillTag';
import type { ISelectedChartTagData } from '../../../ChartPopover/components/SelectedChartTag';

export interface IUseAgentModeSelectorAgentsOptions {
  authorizedAgents?: IAgentData[];
  selectedAgentId?: string;
  handleAgentClick: (agentId: string) => void;
  effectiveTemplate: string;
  urlTab?: string;
  selectedKnowledge: ISelectedKnowledgeTagData[];
  selectedCharts?: ISelectedChartTagData[];
  onSelectedKnowledgeChange?: (items: ISelectedKnowledgeTagData[]) => void;
  onSelectedSkillsChange?: (skills: ISelectedSkillTagData[]) => void;
  onChartConfirm?: (charts: ISelectedChartTagData[], gameCode: string) => void;
  /** Skill/Chart 选中后关闭 Agent/Thinking 下拉，避免 activeKey 泄漏 */
  closeAgentPopovers: () => void;
}

export interface IUseAgentModeSelectorAgentsResult {
  /** 按场景（template/tab）过滤后的 Agent 列表，用于可见性判断与场景自动重置 */
  filteredAgents: IAgentData[];
  /** Agent 下拉实际展示的列表（在 filteredAgents 基础上叠加知识库过滤） */
  agentSelectorList: IAgentData[];
  handleKnowledgeSelectedChange: (items: ISelectedKnowledgeTagData[]) => void;
  handleSkillsChange: (skills: ISelectedSkillTagData[]) => void;
  handleChartConfirm: (
    newCharts: ISelectedChartTagData[],
    gameCode: string,
  ) => void;
}

/**
 * AgentModeSelector 的 Agent 列表与 Mention 联动逻辑：
 * - 场景过滤 + 知识库过滤
 * - 场景切换时自动重置 Agent（含外部显式设置锁）
 * - Skill/Chart/Knowledge 选中时的回调包装
 */
export const useAgentModeSelectorAgents = ({
  authorizedAgents,
  selectedAgentId,
  handleAgentClick,
  effectiveTemplate,
  urlTab,
  selectedKnowledge,
  selectedCharts,
  onSelectedKnowledgeChange,
  onSelectedSkillsChange,
  onChartConfirm,
  closeAgentPopovers,
}: IUseAgentModeSelectorAgentsOptions): IUseAgentModeSelectorAgentsResult => {
  const sceneKey = (effectiveTemplate || urlTab || '') as string;

  const filteredAgents = useMemo(
    () => filterAgentsByScene(authorizedAgents, sceneKey),
    [authorizedAgents, sceneKey],
  );

  const agentSelectorList = useMemo(
    () =>
      filterAgentsForKnowledgeSelection(
        filteredAgents,
        selectedKnowledge.length > 0,
      ),
    [filteredAgents, selectedKnowledge],
  );

  useDebounceEffect(
    () => {
      const { getItem: getExternalAgentLock, setItem: setExternalAgentLock } =
        store;
      if (!filteredAgents?.length) return;
      const isSelectedInList = filteredAgents.some(
        (agent) => agent?.agentId === selectedAgentId,
      );
      if (isSelectedInList) return;

      const externalAgentLock = getExternalAgentLock(
        'externalTargetAgentId',
        '',
      );
      const isExternalSet =
        externalAgentLock && externalAgentLock === selectedAgentId;
      if (isExternalSet) {
        setExternalAgentLock('externalTargetAgentId', '');
        const firstId = filteredAgents[0]?.agentId;
        if (firstId) handleAgentClick(firstId);
        return;
      }

      const firstId = filteredAgents[0]?.agentId;
      if (firstId) handleAgentClick(firstId);
    },
    [filteredAgents, selectedAgentId, authorizedAgents],
    { wait: 0 },
  );

  const handleKnowledgeSelectedChange = useMemoizedFn(
    (items: ISelectedKnowledgeTagData[]) => {
      onSelectedKnowledgeChange?.(items);
      const list = filterAgentsForKnowledgeSelection(
        filteredAgents,
        items.length > 0,
      );
      ensureAgentInKnowledgeAgentList(list, selectedAgentId, handleAgentClick);
    },
  );

  const handleSkillsChange = useMemoizedFn((skills: ISelectedSkillTagData[]) => {
    onSelectedSkillsChange?.(skills);
    if (skills.length > 0) {
      closeAgentPopovers();
    }
  });

  const handleChartConfirm = useMemoizedFn(
    (newCharts: ISelectedChartTagData[], gameCode: string) => {
      onChartConfirm?.(newCharts, gameCode);
      const otherGameCharts = (selectedCharts ?? []).filter(
        (c) => c.gameCode !== gameCode,
      );
      const nextCharts =
        newCharts.length === 0
          ? otherGameCharts
          : [...otherGameCharts, ...newCharts];
      if (nextCharts.length > 0) {
        closeAgentPopovers();
      }
    },
  );

  return {
    filteredAgents,
    agentSelectorList,
    handleKnowledgeSelectedChange,
    handleSkillsChange,
    handleChartConfirm,
  };
};
